import React, { useEffect, useState } from 'react';
import { X, BookOpen, Send } from 'lucide-react';
import { Button } from './ui/button';
import { fetchGuestbook, postGuestbook } from '../lib/community';
import { isBirthdayToday } from '../lib/eggs';

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch (e) { return ''; }
};

// Visitors' guestbook: read messages and leave one. Stored by the /api/guestbook Netlify Function.
const GuestbookModal = ({ isOpen, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable
  const [form, setForm] = useState({ name: '', message: '', website: '' });
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'ok' | 'error', text }

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setStatus('loading');
    fetchGuestbook()
      .then((data) => { if (!cancelled) { setEntries(data.entries || []); setStatus('ready'); } })
      .catch(() => { if (!cancelled) setStatus('unavailable'); });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setNotice(null);
    try {
      const { entry } = await postGuestbook(form);
      if (entry) setEntries((list) => [entry, ...list]);
      setForm({ name: form.name, message: '', website: '' });
      setNotice({ type: 'ok', text: 'Thank you for signing the guestbook!' });
    } catch (err) {
      setNotice({ type: 'error', text: err.status ? err.message : 'Could not send right now. Please try again later.' });
    } finally {
      setSending(false);
    }
  };

  const field = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen size={20} className="text-amber-700" /> Guestbook
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </Button>
        </div>

        {isBirthdayToday() && (
          <div className="px-4 py-2 bg-pink-50 border-b text-sm text-pink-800">🎂 It's David's birthday today. Leave him a birthday wish!</div>
        )}
        <form onSubmit={submit} className="p-4 border-b bg-amber-50/60 space-y-2">
          <input
            className={field}
            placeholder="Your name"
            maxLength={40}
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <textarea
            className={`${field} resize-none`}
            placeholder="Leave a message for the artist…"
            rows={3}
            maxLength={500}
            required
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          {/* Honeypot for spam bots: hidden from people, bots fill it in. */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />
          <div className="flex items-center justify-between gap-2">
            <span className={`text-xs ${notice?.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>{notice?.text}</span>
            <Button type="submit" disabled={sending || status === 'unavailable'} className="bg-amber-600 hover:bg-amber-700 text-white">
              <Send size={16} className="mr-2" /> {sending ? 'Sending…' : 'Sign'}
            </Button>
          </div>
        </form>

        <div className="overflow-y-auto p-4 space-y-3">
          {status === 'loading' && <p className="text-sm text-gray-500">Loading messages…</p>}
          {status === 'unavailable' && <p className="text-sm text-gray-500">The guestbook is not available right now.</p>}
          {status === 'ready' && entries.length === 0 && <p className="text-sm text-gray-500">No messages yet. Be the first to sign!</p>}
          {entries.map((e) => (
            <div key={e.id} className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-gray-800">{e.name}</span>
                <span className="text-xs text-gray-400">{formatDate(e.date)}</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-line break-words mt-1">{e.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GuestbookModal;
